// @/components/reusable/page-header.jsx
// V2 Page Header Component

import React from "react";
import clsx from "clsx";
import { InfoPopover } from "./info-popover";
import { Text } from "@/components/reusable/typography";

// ============================================================================
// PAGE HEADER
// ============================================================================

/**
 * Renders title with badge(s) attached to the last word
 * This ensures the badge never breaks to a new line alone
 *
 * Layout: "First Words " + <span nowrap>"LastWord" + badge(s)</span>
 */
const TitleWithBadge = ({ title, badge, titleBadge }) => {
  // If title is a string, split and attach badges to last word
  if (typeof title === 'string') {
    const words = title.split(' ');
    const lastWord = words.pop();
    const firstPart = words.join(' ');

    return (
      <>
        {firstPart && <>{firstPart} </>}
        <span className="inline-flex items-center whitespace-nowrap">
          {lastWord}
          {badge && <span className="ml-2 -mt-2 inline-flex align-middle">{badge}</span>}
          {titleBadge && <span className="ml-2">{titleBadge}</span>}
        </span>
      </>
    );
  }

  // If title is a React element, render with flex wrapper
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      {title}
      {badge}
      {titleBadge}
    </span>
  );
};

/**
 * Renders subtitle with info icon attached to the last word
 * Same "nowrap last word" trick as TitleWithBadge — prevents the icon from orphaning on its own line
 *
 * Layout: "First words " + <span nowrap>"lastword" + info icon</span>
 */
const SubtitleWithInfo = ({ subtitle, subtitlePrefix, infoContent }) => {
  if (!subtitle && infoContent) {
    return <InfoPopover content={infoContent} trigger="hover" />;
  }

  if (!infoContent) {
    return <>{subtitlePrefix && <><span className="font-bold">{subtitlePrefix}</span>{' '}</>}{subtitle}</>;
  }

  // If subtitle is a string, split and attach info icon to last word
  if (typeof subtitle === 'string') {
    const words = subtitle.split(' ');
    const lastWord = words.pop();
    const firstPart = words.join(' ');

    return (
      <>
        {subtitlePrefix && <><span className="font-bold">{subtitlePrefix}</span>{' '}</>}
        {firstPart && <>{firstPart} </>}
        <span className="inline-flex items-center whitespace-nowrap">
          {lastWord}
          <InfoPopover content={infoContent} trigger="hover" className="ml-1.5" />
        </span>
      </>
    );
  }

  // If subtitle is a React element, render with flex wrapper
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {subtitle}
      <InfoPopover content={infoContent} trigger="hover" />
    </span>
  );
};

/**
 * PageHeader - Responsive header with title, subtitle, badge, status, info popover, and actions
 *
 * Layout: Left side grows/shrinks, right side (actions) never shrinks
 *
 * @param {string|ReactNode} title - Page title text
 * @param {string} subtitle - Optional subtitle/description
 * @param {ReactNode} badge - Optional badge element (renders inline with title, after last word)
 * @param {ReactNode} titleBadge - Optional additional badge element (renders inline with title, after badge)
 * @param {ReactNode} status - Optional status line (renders below actions on right side)
 * @param {string} infoContent - HTML content for info popover
 * @param {ReactNode} actions - Right-side action buttons
 */
const PageHeader = ({
  title,
  subtitle,
  subtitlePrefix,
  badge,
  titleBadge,
  status,
  infoContent,
  actions,
  className,
}) => {
  // Breakpoints here are CONTAINER-relative (@2xl = 42rem of available width),
  // not viewport-relative. The reduction-coach panel narrows this header's
  // container without changing the viewport, so a `sm:` media query still
  // reports "roomy" and keeps the title and actions side by side — which
  // crushes the title to one word per line. A container query sees the real
  // space and stacks instead.
  return (
    <header className={clsx("@container", className)}>
      <div className="@2xl:flex @2xl:items-center @2xl:justify-between">
      {/* Left side - grows to fill, can shrink */}
      <div className="@2xl:w-0 @2xl:flex-1 @2xl:pr-12">
        <h1>
          <TitleWithBadge title={title} badge={badge} titleBadge={titleBadge} />
        </h1>
        {(subtitle || infoContent) && (
          // Use a <div> here, not <Text> (which is a <p>). The
          // SubtitleWithInfo can include an InfoPopover whose HeadlessUI
          // Popover panel renders a <div> via Portal — and React's
          // validateDOMNesting flags the <div>-in-<p> tree before the
          // portal hoists the panel to document.body. Match <Text>/<p>
          // typography explicitly (the `p` rule in typography.css doesn't
          // reach this <div>), so subtitles aren't left unstyled.
          <div data-slot="text" className="text-pretty text-base/5 font-normal mt-1 ml-[2px]">
            <SubtitleWithInfo subtitle={subtitle} subtitlePrefix={subtitlePrefix} infoContent={infoContent} />
          </div>
        )}
      </div>

      {/* Right side - never shrinks once there is room to sit alongside */}
      {(actions || status) && (
        <div className="flex flex-col items-center mt-4 @2xl:ml-6 @2xl:mt-0 @2xl:shrink-0 relative">
          <div className={status ? "@2xl:-mt-7" : ""}>{actions}</div>
          {status && (
            <div className="@2xl:absolute @2xl:top-6 mt-2 @2xl:mt-0 text-xs/5 text-center">
              {status}
            </div>
          )}
        </div>
      )}
      </div>
    </header>
  );
};

// ============================================================================
// SECTION HEADER
// ============================================================================

/**
 * SectionHeader - Smaller header for page sections / step titles.
 *
 * @param {string|ReactNode} title - Section title
 * @param {string} subtitle - Optional subtitle
 * @param {string} infoContent - HTML content for info popover (same as PageHeader)
 * @param {ReactNode} actions - Right-side actions
 */
const SectionHeader = ({
  title,
  subtitle,
  infoContent,
  actions,
  className,
}) => {
  return (
    <div className={clsx("flex items-start gap-4", className)}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-pretty">{title}</h4>
          {infoContent && <InfoPopover content={infoContent} trigger="hover" className="ml-0.5" />}
        </div>
        {subtitle && <Text data-slot="text">{subtitle}</Text>}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
};

// ============================================================================
// SECTION DIVIDER
// ============================================================================

/**
 * SectionDivider - Dashed line with title overlapping on the left, optional action on the right.
 *
 * Used to visually separate page sections (e.g. SA overview, CA update).
 * Title sits on a white background over the dashed line.
 *
 * @param {string|ReactNode} title - Section title
 * @param {ReactNode} actions - Right-side element (button, indicator, etc.)
 */
const SectionDivider = ({
  title,
  actions,
  className,
}) => {
  return (
    <div className={clsx("relative", className)}>
      <div aria-hidden="true" className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-zinc-200 border-dashed" />
      </div>
      <div className="relative flex items-center justify-between">
        <div className="bg-zinc-50 pr-4 leading-none -mt-1 flex flex-col">
          <h4>{title}</h4>
        </div>
        {actions && (
          <div className="bg-zinc-50 pl-4">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export { PageHeader, SectionHeader, SectionDivider };
export default PageHeader;
